import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

// ── Data Quality ────────────────────────────────────────

export function useDataQualitySummary() {
  return useQuery({
    queryKey: ['data-quality', 'summary'],
    queryFn: () => api.get('/api/data-quality/summary').then((r) => r.data),
  });
}

export function useDataQualityRecords(flag: string, page: number) {
  return useQuery({
    queryKey: ['data-quality', 'records', flag, page],
    queryFn: () =>
      api.get('/api/data-quality/records', { params: { flag, page, limit: 50 } }).then((r) => r.data),
    enabled: !!flag,
  });
}

export function useMarkReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; note: string; table: string }) =>
      api
        .put(`/api/data-quality/records/${data.id}/mark-reviewed`, {
          note: data.note,
          table: data.table,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-quality'] }),
  });
}

// ── Suppliers ────────────────────────────────────────────

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get('/api/suppliers').then((r) => r.data),
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.post('/api/suppliers', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/api/suppliers/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

// ── Stone Types ──────────────────────────────────────────

export function useStoneTypes() {
  return useQuery({
    queryKey: ['stone-types'],
    queryFn: () => api.get('/api/stone-types').then((r) => r.data),
  });
}

export function useCreateStoneType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.post('/api/stone-types', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stone-types'] }),
  });
}

export function useUpdateStoneType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/api/stone-types/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stone-types'] }),
  });
}

// ── Packages ─────────────────────────────────────────────

export function usePackagesList() {
  return useQuery({
    queryKey: ['packages-list'],
    queryFn: () => api.get('/api/packages').then((r) => r.data),
  });
}

export function usePackageDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['package', id],
    queryFn: () => api.get(`/api/packages/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.post('/api/packages', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['packages-list'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useCreateStones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, stones }: { packageId: string; stones: any[] }) =>
      api.post(`/api/packages/${packageId}/stones/batch`, { stones }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['packages-list'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

// ── Sawing ───────────────────────────────────────────────

export function useSawStone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stoneId, ...data }: any) =>
      api.post(`/api/stones/${stoneId}/saw`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analytics'] }),
  });
}

// ── Pieces ───────────────────────────────────────────────

export function useUnassignedPieces() {
  return useQuery({
    queryKey: ['pieces', 'unassigned'],
    queryFn: () => api.get('/api/pieces/unassigned').then((r) => r.data),
  });
}

export function usePiecesWithPolishers() {
  return useQuery({
    queryKey: ['pieces', 'with-polishers'],
    queryFn: () => api.get('/api/pieces/with-polishers').then((r) => r.data),
  });
}

export function usePiecesAwaitingExport() {
  return useQuery({
    queryKey: ['pieces', 'awaiting-export'],
    queryFn: () => api.get('/api/pieces/awaiting-export').then((r) => r.data),
  });
}

export function useAssignPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, ...data }: any) =>
      api.post(`/api/pieces/${pieceId}/assign`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pieces'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useCompletePiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, ...data }: any) =>
      api.post(`/api/pieces/${pieceId}/complete`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pieces'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useRejectPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, ...data }: any) =>
      api.post(`/api/pieces/${pieceId}/reject`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pieces'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useExportPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, ...data }: any) =>
      api.post(`/api/pieces/${pieceId}/export`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pieces'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useExportBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { pieceIds: string[]; exportDate?: string; jointLotNumber?: string }) =>
      api.post('/api/pieces/export-batch', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pieces'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

// ── Workers ──────────────────────────────────────────────

export function useWorkers() {
  return useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get('/api/workers').then((r) => r.data),
  });
}

export function useCreateWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.post('/api/workers', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workers'] }),
  });
}

export function useUpdateWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/api/workers/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workers'] }),
  });
}

// ── QC Operations ───────────────────────────────────────

export function useVerifyPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, ...data }: { packageId: string; verifiedPieceCount: number; verifiedWeightCt: number; qualityAssessment?: string }) =>
      api.put(`/api/qc/packages/${packageId}/verify`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qc'] });
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useConfirmHandoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ handoffId, weightInCt }: { handoffId: string; weightInCt: number }) =>
      api.put(`/api/qc/handoffs/${handoffId}/confirm`, { weightInCt }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qc'] });
    },
  });
}

// ── Worker Self-Service ──────────────────────────────────

export function useMyPieces() {
  return useQuery({
    queryKey: ['worker', 'my-pieces'],
    queryFn: () => api.get('/api/worker/my-pieces').then((r) => r.data),
    refetchInterval: 30_000,
  });
}

export function useConfirmReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, receivedWeightCt }: { pieceId: string; receivedWeightCt: number }) =>
      api.post(`/api/worker/pieces/${pieceId}/confirm`, { receivedWeightCt }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['worker', 'my-pieces'] }),
  });
}

export function useSubmitPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, ...data }: any) =>
      api.post(`/api/worker/pieces/${pieceId}/submit`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['worker', 'my-pieces'] }),
  });
}
